import { Button } from '@suscri/ui';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PageHeader } from '../components/ui/PageHeader';
import { dateLocale } from '../lib/i18n';
import { trpc } from '../lib/trpc';

// Vista mínima de los campos que renderiza esta página. El tipo inferido
// completo de upcomingRenewals dispara TS2589 (instanciación excesivamente
// profunda) por los tipos Decimal de Prisma al atravesar la serialización tRPC.
type UpcomingRenewal = {
  id: string;
  name: string;
  nextRenewal: string | Date;
  amount: string | number;
};

export function CalendarPage() {
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const navigate = useNavigate();

  const { data: upcomingRenewals, isLoading } =
    trpc.subscriptions.upcomingRenewals.useQuery({
      days: 30,
    });
  const renewalsList = (upcomingRenewals ?? []) as unknown as UpcomingRenewal[];

  const goToPreviousMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
    );
  };

  const goToNextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
    );
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleRenewalClick = (subscriptionId: string) => {
    navigate(`/subscriptions?highlight=${subscriptionId}`);
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    // Convertir el día de la semana para que Lunes = 0, Martes = 1, etc.
    let startingDayOfWeek = firstDay.getDay();
    if (startingDayOfWeek === 0) {
      startingDayOfWeek = 6; // Domingo se convierte en 6
    } else {
      startingDayOfWeek = startingDayOfWeek - 1; // Lunes = 0, Martes = 1, etc.
    }

    const days = [];

    // Días del mes anterior
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const day = new Date(year, month, -i);
      days.push({ date: day, isCurrentMonth: false, isToday: false });
    }

    // Días del mes actual
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isToday = date.toDateString() === new Date().toDateString();
      days.push({ date, isCurrentMonth: true, isToday });
    }

    // Días del mes siguiente para completar la cuadrícula
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      days.push({ date, isCurrentMonth: false, isToday: false });
    }

    return days;
  };

  const days = getDaysInMonth(currentDate);
  // Nombre del mes y días de la semana según el idioma activo
  const monthLabel = currentDate.toLocaleDateString(dateLocale(), {
    month: 'long',
    year: 'numeric',
  });
  const weekDays = [
    t('Lun'),
    t('Mar'),
    t('Mié'),
    t('Jue'),
    t('Vie'),
    t('Sáb'),
    t('Dom'),
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t('Renovaciones')}
        title={t('Calendario')}
        description={t('Visualiza las renovaciones de tus suscripciones')}
        actions={<Button onClick={goToToday}>{t('Hoy')}</Button>}
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="surface-card relative overflow-hidden p-4 sm:p-6">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-md">
                    <Calendar className="h-5 w-5 text-white" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {monthLabel}
                  </h2>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={goToPreviousMonth}
                    className="hover:bg-blue-50 hover:border-blue-300 transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={goToNextMonth}
                    className="hover:bg-blue-50 hover:border-blue-300 transition-colors"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-1 mb-2">
                {weekDays.map(day => (
                  <div
                    key={day}
                    className="p-3 text-center text-sm font-semibold text-gray-600 bg-gray-50 rounded-lg"
                  >
                    {day}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {days.map((day, index) => (
                  <div
                    key={index}
                    className={`p-3 min-h-[50px] rounded-lg transition-all duration-200 ${
                      day.isCurrentMonth
                        ? 'bg-white hover:bg-blue-50 border border-gray-200 hover:border-blue-300 hover:shadow-md'
                        : 'bg-gray-100 text-gray-400'
                    } ${
                      day.isToday
                        ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-xl ring-4 ring-blue-300 ring-opacity-50 border-2 border-blue-400 font-bold transform scale-105'
                        : ''
                    }`}
                  >
                    <div className="text-sm font-semibold flex items-center justify-center relative">
                      {day.date.getDate()}
                      {day.isToday && (
                        <div className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="surface-card relative overflow-hidden p-4 sm:p-6">
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-emerald-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative z-10">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center shadow-md">
                  <Calendar className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Próximas Renovaciones
                </h3>
              </div>
              {isLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-16 bg-gray-200 animate-pulse rounded-lg"
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {renewalsList.slice(0, 5).map(subscription => (
                    <div
                      key={subscription.id}
                      onClick={() => handleRenewalClick(subscription.id)}
                      className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 cursor-pointer"
                    >
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-md">
                        <Calendar className="h-5 w-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {subscription.name}
                        </p>
                        <p className="text-xs text-gray-600">
                          {new Date(
                            subscription.nextRenewal
                          ).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-sm font-bold text-gray-900">
                        €{subscription.amount}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
